import React, { useState, useRef, useEffect } from "react";
import "./TopBar.css";
import { DEVICES, DEVICE_SIZES } from "../../constants/breakpoints";
import PublishModal from "./PublishModal";

function TopBar({ 
  toggleCodeView, 
  selectedDevice, 
  onDeviceSelect, 
  onSave, 
  toggleLivePreview, 
  onPublish, 
  onLiveBuild, 
  toggleCMS,
  selectedContentItem,
  onContentItemSelect,
  contentItems,
  currentPage,
  pages,
  zoomLevel,
  setZoomLevel,
  siteId
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleContentItemSelect = (item) => {
    console.log("TopBar selecting content item:", item);
    onContentItemSelect(item);
    setDropdownOpen(false);
  };

  const handleGenerateWithAI = () => {
    // Add AI generation functionality here
    console.log("Generate with AI clicked");
  };

  const handlePublishClick = () => {
    setPublishModalOpen(true);
  };

  const handlePublishModalClose = () => {
    setPublishModalOpen(false);
  };

  const handlePublish = async (publishData) => {
    try {
      // Call the original onPublish function with the publish data
      const result = await onPublish(publishData);
      return result;
    } catch (error) {
      console.error('Error in publish:', error);
      throw error;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Check if current page is a template page
  const currentPageData = pages?.find(page => page.id === currentPage);
  const isTemplatePage = currentPageData?.isTemplate;

  // Get display name for selected content item
  const getContentItemDisplayName = () => {
    if (!selectedContentItem) return "Select content...";
    
    // Use the first field as the display name
    const firstField = Object.keys(selectedContentItem.data)[0];
    return selectedContentItem.data[firstField] || "Untitled";
  };

  // Debug logging to check selectedContentItem
  useEffect(() => {
    if (isTemplatePage && contentItems && contentItems.length > 0) {
      console.log("TopBar - Current selected content item:", selectedContentItem);
    }
  }, [selectedContentItem, isTemplatePage, contentItems]);

  return (
    <>
      <div className="top-bar">
        <div className="topbar-left-section">
          <button 
            className="hamburger-btn"
            onClick={toggleMenu}
            title="Menu"
          >
            <i className="fas fa-bars"></i>
          </button>

          <button 
            className="ai-generate-btn"
            onClick={handleGenerateWithAI}
            title="Generate content using AI"
          >
            <i className="fas fa-magic"></i>
            <span>Generate with AI</span>
          </button>

          {isTemplatePage && contentItems && contentItems.length > 0 && (
            <div className="content-selector-wrapper" ref={dropdownRef}>
              <button 
                className="content-selector-btn"
                onClick={toggleDropdown}
                title="Select content item"
              >
                <i className="fas fa-database"></i>
                <span>{getContentItemDisplayName()}</span>
                <i className={`fas fa-chevron-${dropdownOpen ? 'up' : 'down'}`}></i>
              </button>
              
              {dropdownOpen && (
                <div className="content-dropdown">
                  <div className="content-dropdown-header">
                    <i className="fas fa-database"></i>
                    <span>Content Items</span>
                  </div>
                  <div className="content-dropdown-items">
                    {contentItems.map(item => {
                      const isSelected = selectedContentItem && selectedContentItem._id === item._id;
                      const displayName = item.data[Object.keys(item.data)[0]] || 'Untitled';
                      
                      return (
                        <button 
                          key={item._id} 
                          className={`content-item-btn ${isSelected ? 'active' : ''}`}
                          onClick={() => handleContentItemSelect(item)}
                        >
                          <span>{displayName}</span>
                          {isSelected && (
                            <i className="fas fa-check"></i>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {menuOpen && (
            <div className="menu-dropdown">
              <ul>
                <li>
                  <button className="menu-item">
                    <i className="fas fa-tachometer-alt"></i>
                    <span>Dashboard</span>
                  </button>
                </li>
                <li>
                  <button className="menu-item">
                    <i className="fas fa-project-diagram"></i>
                    <span>Projects</span>
                  </button>
                </li>
                <li>
                  <button className="menu-item">
                    <i className="fas fa-cog"></i>
                    <span>Settings</span>
                  </button>
                </li>
                <li>
                  <button className="menu-item">
                    <i className="fas fa-question-circle"></i>
                    <span>Help</span>
                  </button>
                </li>
                <li className="divider"></li>
                <li>
                  <button className="menu-item">
                    <i className="fas fa-sign-out-alt"></i>
                    <span>Logout</span>
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
        
        <div className="device-switcher">
          <button 
            className={`device-btn ${selectedDevice === DEVICES.DESKTOP ? 'active' : ''}`}
            onClick={() => onDeviceSelect(DEVICES.DESKTOP)}
            title="Desktop View"
          >
            <i className="fas fa-desktop"></i>
          </button>
          <button 
            className={`device-btn ${selectedDevice === DEVICES.TABLET ? 'active' : ''}`}
            onClick={() => onDeviceSelect(DEVICES.TABLET)}
            title="Tablet View"
          >
            <i className="fas fa-tablet-alt"></i>
          </button>
          <button 
            className={`device-btn ${selectedDevice === DEVICES.MOBILE ? 'active' : ''}`}
            onClick={() => onDeviceSelect(DEVICES.MOBILE)}
            title="Mobile View"
          >
            <i className="fas fa-mobile-alt"></i>
          </button>
        </div>

        <div className="zoom-controls">
          <button 
            className="zoom-btn"
            onClick={() => setZoomLevel(Math.max(0.25, zoomLevel - 0.25))}
            title="Zoom Out"
          >
            <i className="fas fa-search-minus"></i>
          </button>
          <span className="zoom-level">{Math.round(zoomLevel * 100)}%</span>
          <button 
            className="zoom-btn"
            onClick={() => setZoomLevel(Math.min(2, zoomLevel + 0.25))}
            title="Zoom In"
          >
            <i className="fas fa-search-plus"></i>
          </button>
        </div>

        <div 
          className="topbar-right-section" 
          style={{ 
            position: 'absolute', 
            right: '12px',
            display: 'flex',
            gap: '6px',
            alignItems: 'center'
          }}
        >
          <button 
            onClick={onLiveBuild}
            title="Toggle Live Preview"
            style={{
              height: '26px',
              borderRadius: '4px',
              border: '1px solid #333333',
              backgroundColor: '#2a2a2a',
              color: '#ffffff',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontSize: '11px',
              padding: '0 10px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#363636';
              e.target.style.borderColor = '#444444';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#2a2a2a';
              e.target.style.borderColor = '#333333';
            }}
          >
            <i className="fas fa-eye" style={{ fontSize: '12px' }}></i>
            <span>Preview</span>
          </button>
          
          <button 
            onClick={onSave}
            title="Save"
            style={{
              height: '26px',
              borderRadius: '4px',
              border: '1px solid #333333',
              backgroundColor: '#2a2a2a',
              color: '#ffffff',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontSize: '11px',
              padding: '0 10px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#363636';
              e.target.style.borderColor = '#444444';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#2a2a2a';
              e.target.style.borderColor = '#333333';
            }}
          >
            <i className="fas fa-save" style={{ fontSize: '12px' }}></i>
            <span>Save</span>
          </button>
          
          <button 
            onClick={handlePublishClick}
            title="Publish"
            style={{
              height: '26px',
              borderRadius: '4px',
              border: '1px solid #0063b1',
              backgroundColor: '#0078d4',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontSize: '11px',
              padding: '0 10px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#0086f0';
              e.target.style.borderColor = '#0078d4';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#0078d4';
              e.target.style.borderColor = '#0063b1';
            }}
          >
            <i className="fas fa-rocket" style={{ fontSize: '12px' }}></i>
            <span>Publish</span>
          </button>
        </div>
      </div>

      <PublishModal
        isOpen={publishModalOpen}
        onClose={handlePublishModalClose}
        onPublish={handlePublish}
        siteId={siteId}
      />
    </>
  );
}

export default TopBar;